import {
  AD_GROUP_NAME_MAX_LENGTH,
  CreateAdGroupSchema,
  PricingRuleCommonInputSchema,
} from '@hprofits/api/ad-group/schemas';
import type { PricingRuleOutput } from '@hprofits/api/ad-group/types';
import { adGroupKeys, useCopyAdGroup } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import type { AdGroupId, CampaignGroupId, CreativeGroupId, TargetGroupId } from '@hprofits/shared/types';
import type { SelectOption } from '@hprofits/ui';
import type { SmartFormFieldDef, UseSmartFormDialogFn } from '@hprofits/ui-smart-controls';
import { defineSmartFields, useSmartFormDialog } from '@hprofits/ui-smart-controls';
import { useQueryClient } from '@tanstack/vue-query';
import { MaybeRefOrGetter, Ref, ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { z } from 'zod';

import { apiClient } from '@/api-client/client';
import { t } from '@/i18n';
import { nextNewName } from '@/modules/campaigns/components/Layout/composables';
import { justCreatedState } from '@/modules/campaigns/composables/useOnJustCreated';
import {
  getAdGroupCopyGenericErrorMessage,
  getAdGroupCopyMutationErrorMessage,
  getAdGroupCopyMutationErrorOverride,
  getAdGroupCopyNoCreativeGroupsMessage,
  getAdGroupCopyNoRuleMessage,
} from '@/modules/campaigns/utils/mutation-errors/adGroup';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';
import type { UsePageControllerResult } from '@/modules/common/state/usePageController';
import { createMutationErrorHandler } from '@/modules/shared/composables';

// Same field trio as the create dialog, but the copy keeps the source's
// target group: its select is rendered locked, for context only, and the
// value never reaches the copy request (the input has no targetGroupId).

export const getAdGroupCopyCreativeRequiredMessage = (): string => t('modules.campaigns.actions.selectCreativeGroup');

const DialogSchema = z.object({
  name: CreateAdGroupSchema.unwrap().shape.name,
  targetGroupId: PricingRuleCommonInputSchema.unwrap().shape.targetGroupId,
  // Piped through a plain number so an unset Select reports the line above
  // instead of zod's "expected number, received null"; the brand and the range
  // still come from the API schema.
  creativeGroupId: z
    .number({ error: () => getAdGroupCopyCreativeRequiredMessage() })
    .pipe(PricingRuleCommonInputSchema.unwrap().shape.creativeGroupId),
});

type AdGroupCopyForm = z.infer<typeof DialogSchema>;

/**
 * What the dialog holds while open. The creative group starts unset unless the
 * campaign has exactly one — swapping it is the reason to duplicate at all, so
 * seeding the source's value invites cloning onto the wrong one by accident.
 * `DialogSchema` rejects the null at submit, so `onSubmit` still receives the
 * complete `AdGroupCopyForm`.
 */
type AdGroupCopyDraft = Omit<AdGroupCopyForm, 'creativeGroupId'> & { creativeGroupId: CreativeGroupId | null };

const adGroupCopyFields = defineSmartFields<AdGroupCopyDraft>();

const NAME_FIELD: SmartFormFieldDef = adGroupCopyFields.text('name', {
  get label() {
    return t('modules.campaigns.actions.adGroupFields.name');
  },
});

type TargetSelectOption = SelectOption<TargetGroupId>;
type CreativeSelectOption = SelectOption<CreativeGroupId>;

const buildFields = (
  targetOption: TargetSelectOption,
  creativeOptions: ReadonlyArray<CreativeSelectOption>,
): ReadonlyArray<SmartFormFieldDef> => [
  NAME_FIELD,
  adGroupCopyFields.select.list('targetGroupId', {
    label: t('modules.campaigns.actions.adGroupFields.targetGroup'),
    options: [targetOption],
    clearable: false,
    readonly: true,
  }),
  adGroupCopyFields.select.list('creativeGroupId', {
    label: t('modules.campaigns.actions.adGroupFields.creativeGroup'),
    options: creativeOptions,
    clearable: false,
    highlightWhen: (values) => creativeOptions.length > 1 && values.creativeGroupId == null,
  }),
];

export type AdGroupCopySource = Readonly<{
  id: AdGroupId;
  name: string;
}>;

type TargetGroupOptionSource = Readonly<{
  id: TargetGroupId;
  name: string;
}>;

export type UseAdGroupCopyDialogDeps = Readonly<{
  campaignGroupId: CampaignGroupId;
  existingAdNames: MaybeRefOrGetter<ReadonlyArray<string>>;
  targetGroups: MaybeRefOrGetter<ReadonlyArray<TargetGroupOptionSource>>;
  creativeOptions: MaybeRefOrGetter<ReadonlyArray<CreativeSelectOption>>;
  pageController: UsePageControllerResult;
}>;

export type UseAdGroupCopyDialogReturn = Readonly<{
  open: (source: AdGroupCopySource) => Promise<void>;
  /**
   * Id of the ad group whose detail is being fetched, or null — drives the
   * per-row spinner. Covers only the pre-dialog fetch: it is cleared before
   * the dialog opens; the submit itself is guarded by the modal dialog.
   */
  copyingAdGroupId: Readonly<Ref<AdGroupId | null>>;
}>;

export const useAdGroupCopyDialog = ({
  campaignGroupId,
  existingAdNames,
  targetGroups,
  creativeOptions,
  pageController,
}: UseAdGroupCopyDialogDeps): UseAdGroupCopyDialogReturn => {
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();
  const queryClient = useQueryClient();
  const copyAdGroup = useCopyAdGroup(apiClient, SUPPRESS_ERROR_TOAST_META);
  const openFormDialog: UseSmartFormDialogFn = useSmartFormDialog();
  // The mutation's own isPending cannot tell the sidebar WHICH row is busy.
  const copyingAdGroupId = ref<AdGroupId | null>(null);

  // Covers the pre-dialog source fetch; submit failures render in-dialog.
  const handleSourceLoadError = createMutationErrorHandler({
    errors: pageController.errors,
    title: () => t('modules.campaigns.actions.errors.duplicateAdGroup'),
    override: getAdGroupCopyMutationErrorOverride,
    genericMessage: getAdGroupCopyGenericErrorMessage,
    logTag: '[useAdGroupCopyDialog:sourceLoad]',
  });

  // A rule-less source is not retryable — "Try again" would mislead.
  const handleMissingRule = createMutationErrorHandler({
    errors: pageController.errors,
    title: () => t('modules.campaigns.actions.errors.duplicateAdGroup'),
    override: getAdGroupCopyMutationErrorOverride,
    genericMessage: getAdGroupCopyNoRuleMessage,
    logTag: '[useAdGroupCopyDialog:missingRule]',
  });

  const open = async (source: AdGroupCopySource): Promise<void> => {
    if (copyingAdGroupId.value !== null) {
      return;
    }

    // Options are snapshotted when the dialog opens, so an empty list would
    // leave a form that can never be submitted and never repopulate. Refuse
    // before the source fetch rather than open a dead end.
    if (toValue(creativeOptions).length === 0) {
      pageController.errors.showErrorItem({
        title: t('modules.campaigns.actions.errors.duplicateAdGroup'),
        description: getAdGroupCopyNoCreativeGroupsMessage(),
      });
      return;
    }

    copyingAdGroupId.value = source.id;

    let rule: PricingRuleOutput | undefined;
    try {
      // One-shot TanStack fetch: warms the detail cache the AdGroup page uses.
      const adGroup = await queryClient.fetchQuery({
        queryKey: adGroupKeys.detail(campaignGroupId, source.id),
        queryFn: async () => apiClient.adGroup.findOne({ adGroupId: source.id }),
      });
      rule = adGroup.pricingRules[0];
      if (rule === undefined) {
        handleMissingRule(new Error(`ad group ${String(source.id)} has no pricing rule`));
        return;
      }
    } catch (err) {
      handleSourceLoadError(err);
      return;
    } finally {
      copyingAdGroupId.value = null;
    }

    const targetName =
      toValue(targetGroups).find((tg) => tg.id === rule.targetGroupId)?.name ?? String(rule.targetGroupId);
    const currentCreativeOptions = toValue(creativeOptions);

    const defaultValues: AdGroupCopyDraft = {
      // Same generator as create, seeded with the source name, so a copy of
      // "Popunder EU" becomes "Popunder EU 1". Capped because the seed is
      // user-authored and may already sit at the limit.
      name: nextNewName(source.name, toValue(existingAdNames), AD_GROUP_NAME_MAX_LENGTH),
      targetGroupId: rule.targetGroupId,
      // A single option leaves nothing to swap to, so seeding it costs the user
      // a pointless pick; with several, the choice must be deliberate — the
      // field's `highlightWhen` marks it until they make one.
      creativeGroupId: currentCreativeOptions.length === 1 ? (currentCreativeOptions[0]?.value ?? null) : null,
    };

    await openFormDialog<AdGroupCopyForm, AdGroupCopyDraft>({
      title: t('modules.campaigns.actions.duplicateAdGroup'),
      schema: DialogSchema,
      fields: buildFields({ value: rule.targetGroupId, label: targetName }, currentCreativeOptions),
      defaultValues,
      submitText: t('modules.campaigns.actions.duplicateSubmit'),
      onSubmit: async ({ name, creativeGroupId }) => {
        try {
          const { id } = await copyAdGroup.mutateAsync({
            campaignId: campaignGroupId,
            sourceId: source.id,
            name,
            creativeGroupId,
          });
          const adGroupRoute = toValue(routeResolver.adGroup(campaignGroupId, id));
          await router.push({
            ...adGroupRoute,
            state: justCreatedState(String(id)),
          });
        } catch (err) {
          // Re-throw with a friendly message — useSubmitDialog renders it
          // in-dialog and keeps the dialog open, so the user can e.g. pick a
          // compatible creative group after a 417.
          throw new Error(getAdGroupCopyMutationErrorMessage(err), { cause: err });
        }
      },
    });
  };

  return {
    open,
    copyingAdGroupId,
  };
};
